import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../../../ui/components/Button";
import { FeatherPlus, FeatherEdit, FeatherTrash2, FeatherFileText, FeatherCalendar, FeatherUser, FeatherLoader, FeatherAlertCircle, FeatherRefreshCw, FeatherDownload, FeatherChevronDown, FeatherChevronUp } from "@subframe/core";
import { SavedPivotView } from "../types";
import { getPivotViews, deletePivotView as deletePivotViewApi, getFileHistory, exportPivotToExcel, exportAllPivotTabsToExcel, updatePivotSetName, toggleRegisterSetStatus, FileHistoryEntry } from "../../../../data/api/pivotApi";
import { loadAllPivotViews, deletePivotView as deletePivotViewLocal, syncToLocalStorage } from "../utils/storage";
import { useAuth } from "../../../../../auth";

interface PivotViewLibraryProps {
  onCreateNew: () => void;
}

export const PivotViewLibrary: React.FC<PivotViewLibraryProps> = ({ onCreateNew }) => {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [savedViews, setSavedViews] = useState<SavedPivotView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [fileHistories, setFileHistories] = useState<Record<string, FileHistoryEntry[]>>({});
  const [expandedViews, setExpandedViews] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled'>('all');
  const [togglingFileIds, setTogglingFileIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadViews();
  }, [company?.companyId, statusFilter]);

  // Auto-load file histories for all views after views are loaded
  useEffect(() => {
    if (savedViews.length > 0) {
      loadAllFileHistories();
    }
  }, [savedViews]);

  const loadAllFileHistories = async () => {
    const viewIds = savedViews.map(v => v.id);
    const uniqueViewIds = Array.from(new Set(viewIds));
    
    // Load file history for all views
    for (const viewId of uniqueViewIds) {
      if (!fileHistories[viewId]) {
        try {
          const history = await getFileHistory(viewId);
          setFileHistories(prev => ({ ...prev, [viewId]: history }));
        } catch (err: any) {
          console.error(`Failed to load file history for view ${viewId}:`, err);
        }
      }
    }
  };

  // Aggregate file history from all views in a register set
  const getAggregatedFileHistory = (views: SavedPivotView[]): FileHistoryEntry[] => {
    const allHistories: FileHistoryEntry[] = [];
    const seenFileIds = new Set<string>();
    
    // Collect file history from all views
    for (const view of views) {
      const history = fileHistories[view.id] || [];
      for (const entry of history) {
        // Deduplicate by fileId (same file might be used by multiple tabs)
        if (!seenFileIds.has(entry.fileId)) {
          seenFileIds.add(entry.fileId);
          allHistories.push(entry);
        }
      }
    }
    
    // Sort by usedAt descending (most recent first)
    return allHistories.sort((a, b) => {
      const dateA = new Date(a.usedAt).getTime();
      const dateB = new Date(b.usedAt).getTime();
      return dateB - dateA;
    });
  };

  const loadViews = async () => {
    setLoading(true);
    setError(null);
    try {
      const companyId = company?.companyId ? parseInt(company.companyId) : undefined;
      const views = await getPivotViews(companyId, undefined, statusFilter);
      setSavedViews(views);
      // Sync to localStorage for offline support
      syncToLocalStorage(views);
    } catch (err: any) {
      console.error("Failed to load pivot views:", err);
      setError(err.message || "Failed to load pivot views");
      // Fallback to localStorage
      const cached = loadAllPivotViews();
      setSavedViews(cached);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadViews();
    setRefreshing(false);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deletePivotViewApi(id);
      // Remove from localStorage
      deletePivotViewLocal(id);
      // Refresh list
      await loadViews();
    } catch (err: any) {
      alert(`Failed to delete: ${err.message || "Unknown error"}`);
    }
  };

  const handleUseWithNewData = (viewId: string) => {
    navigate(`/cfo/registers/uploadv1/apply/${viewId}`);
  };

  const handleExportWithLastFile = async (view: SavedPivotView) => {
    if (!view.lastUsedFileId) {
      alert("No file available. Please use 'Use with New Data' first to upload a file.");
      return;
    }

    try {
      // Export using the last used file
      const { exportPivotToExcel } = await import("../../../../data/api/pivotApi");
      await exportPivotToExcel({
        viewId: view.id,
        viewName: view.name,
        options: {
          includeConfigSheet: true,
        },
      });
      alert(`Successfully exported "${view.name}" with last used file!`);
    } catch (error: any) {
      console.error("Failed to export:", error);
      alert(`Failed to export: ${error.message || "Unknown error"}`);
    }
  };

  const handleToggleFileHistory = async (viewId: string) => {
    if (expandedViews.has(viewId)) {
      // Collapse
      setExpandedViews(prev => {
        const newSet = new Set(prev);
        newSet.delete(viewId);
        return newSet;
      });
    } else {
      // Expand - load file history if not already loaded
      if (!fileHistories[viewId]) {
        try {
          const history = await getFileHistory(viewId);
          setFileHistories(prev => ({ ...prev, [viewId]: history }));
        } catch (err: any) {
          console.error("Failed to load file history:", err);
          alert(`Failed to load file history: ${err.message || "Unknown error"}`);
          return;
        }
      }
      setExpandedViews(prev => new Set(prev).add(viewId));
    }
  };

  const handleExportWithFile = async (view: SavedPivotView, fileId: string) => {
    try {
      // Get all views that belong to the same file group (same original file_id)
      // This ensures we export all tabs from the same file upload
      const companyId = company?.companyId ? parseInt(company.companyId) : undefined;
      let allViewsFromFile: SavedPivotView[] = [];
      
      if (view.fileId) {
        // Get all views created from the same original file
        allViewsFromFile = await getPivotViews(companyId, view.fileId);
      } else {
        // If no fileId, just export this single view
        allViewsFromFile = [view];
      }

      if (allViewsFromFile.length > 1) {
        // Export all tabs from the same file
        const viewIds = allViewsFromFile.map(v => v.id);
        await exportAllPivotTabsToExcel({
          fileId: fileId, // Use the file from history (the data source)
          viewIds: viewIds, // All views from the same original file
          viewName: `All Tabs - ${view.name}`,
          options: {
            includeConfigSheet: true,
          },
        });
        alert(`Successfully exported ${allViewsFromFile.length} pivot view(s) from "${fileId}"!`);
      } else {
        // Single view, use regular export
        await exportPivotToExcel({
          viewId: view.id,
          fileId: fileId, // Specify which file to use
          viewName: view.name,
          options: {
            includeConfigSheet: true,
          },
        });
        alert(`Successfully exported "${view.name}"!`);
      }
    } catch (error: any) {
      console.error("Failed to export:", error);
      alert(`Failed to export: ${error.message || "Unknown error"}`);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return dateString;
    }
  };

  // Group views by file_id
  const groupViewsByFile = () => {
    const grouped: Map<string | null, SavedPivotView[]> = new Map();
    
    savedViews.forEach((view) => {
      const key = view.fileId || null; // Use null for views without fileId
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(view);
    });

    // Sort views within each group by tabIndex
    grouped.forEach((views) => {
      views.sort((a, b) => (a.tabIndex || 0) - (b.tabIndex || 0));
    });

    return grouped;
  };

  const handleDeleteGroup = async (views: SavedPivotView[]) => {
    const viewNames = views.map(v => v.name).join(", ");
    if (!window.confirm(`Are you sure you want to delete all ${views.length} pivot views?\n\n${viewNames}\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await Promise.all(views.map(view => deletePivotViewApi(view.id)));
      views.forEach(view => deletePivotViewLocal(view.id));
      await loadViews();
    } catch (err: any) {
      alert(`Failed to delete: ${err.message || "Unknown error"}`);
    }
  };

  const handleRenameGroup = async (fileId: string, views: SavedPivotView[]) => {
    const companyId = company?.companyId ? parseInt(company.companyId) : undefined;
    const primaryView = views[0];

    const currentName =
      views.find((v) => v.setName && v.setName.trim().length > 0)?.setName ||
      "";

    const suggested =
      currentName ||
      `${primaryView.lastLead || "Pivot Views"}${primaryView.lastPeriod ? ` - ${primaryView.lastPeriod}` : ""}`;

    const nextName = window.prompt("Enter a name for this Pivot View set", suggested);
    if (!nextName) return;
    const trimmed = nextName.trim();
    if (!trimmed) return;

    try {
      await updatePivotSetName(fileId, trimmed, companyId);
      setSavedViews((prev) => {
        const updated = prev.map((v) =>
          v.fileId === fileId ? { ...v, setName: trimmed } : v
        );
        syncToLocalStorage(updated);
        return updated;
      });
    } catch (err: any) {
      console.error("Failed to rename pivot view set:", err);
      alert(`Failed to rename: ${err.message || "Unknown error"}`);
    }
  };

  const handleToggleStatus = async (fileId: string, currentStatus: boolean) => {
    if (!fileId) return;
    
    setTogglingFileIds(prev => new Set(prev).add(fileId));
    try {
      const companyId = company?.companyId ? parseInt(company.companyId) : undefined;
      const newStatus = !currentStatus;
      await toggleRegisterSetStatus(fileId, newStatus, companyId);
      
      // Update local state
      setSavedViews((prev) => {
        const updated = prev.map((v) =>
          v.fileId === fileId ? { ...v, isEnabled: newStatus, disabledAt: newStatus ? null : new Date().toISOString() } : v
        );
        syncToLocalStorage(updated);
        return updated;
      });
    } catch (err: any) {
      console.error("Failed to toggle register set status:", err);
      alert(`Failed to ${currentStatus ? 'disable' : 'enable'}: ${err.message || "Unknown error"}`);
    } finally {
      setTogglingFileIds(prev => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex w-full items-center justify-center py-16 px-4">
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center">
            <FeatherLoader className="w-8 h-8 animate-spin text-brand-600" />
          </div>
          <p className="text-body font-body text-subtext-color">Loading Registers..</p>
        </div>
      </div>
    );
  }

  const groupedViews = groupViewsByFile();
  const viewGroups = Array.from(groupedViews.entries());

  return (
    <div className="flex flex-col gap-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-heading-2 font-heading-2 text-default-font mb-1">
            Saved Registers
          </h2>
          <p className="text-body font-body text-subtext-color">
            Create new registers or use existing ones with new monthly data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'disabled')}
            className="px-3 py-2 border border-neutral-border rounded-md bg-default-background text-body font-body text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="all">Show: All</option>
            <option value="active">Show: Active</option>
            <option value="disabled">Show: Disabled</option>
          </select>
          <Button
            variant="neutral-tertiary"
            icon={<FeatherRefreshCw className={refreshing ? "animate-spin" : ""} />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh
          </Button>
          <Button variant="brand-primary" icon={<FeatherPlus />} onClick={onCreateNew}>
            Create New Register
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-warning-50 border border-warning-200">
          <FeatherAlertCircle className="text-warning-600" />
          <span className="text-body font-body text-warning-700">
            {error} (Showing cached data)
          </span>
        </div>
      )}

      {/* Views Grid */}
      {viewGroups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed border-neutral-border rounded-lg bg-neutral-50">
          <FeatherFileText className="w-12 h-12 text-subtext-color mb-4" />
          <h3 className="text-heading-3 font-heading-3 text-default-font mb-2">
            No Registers Yet
          </h3>
          <p className="text-body font-body text-subtext-color mb-6 text-center max-w-md">
            Create your first register configuration to get started. Once saved, you can reuse it with new monthly data.
          </p>
          <Button variant="brand-primary" icon={<FeatherPlus />} onClick={onCreateNew}>
            Create Your First Register
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {viewGroups.map(([fileId, views]) => {
            const isFileGroup = fileId !== null;
            const isGrouped = isFileGroup && views.length > 1;
            const primaryView = views[0]; // Use first view for metadata
            const viewCount = views.length;
            const setName =
              views.find((v) => v.setName && v.setName.trim().length > 0)?.setName ||
              primaryView.setName ||
              "";
            const isEnabled = primaryView.isEnabled !== false; // Default to true if not set
            const isToggling = fileId ? togglingFileIds.has(fileId) : false;
            // Aggregate file history from all views in the register set
            const fileHistory = isGrouped ? getAggregatedFileHistory(views) : (fileHistories[primaryView.id] || []);

            return (
              <div
                key={fileId || `standalone-${primaryView.id}`}
                className={`flex flex-col w-full p-6 border rounded-lg transition-all ${
                  isEnabled
                    ? 'border-neutral-border bg-default-background hover:border-brand-300 hover:shadow-md'
                    : 'border-neutral-200 bg-neutral-50 opacity-60'
                }`}
              >
                {/* Header Row */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <h3 className={`text-heading-3 font-heading-3 ${isEnabled ? 'text-default-font' : 'text-subtext-color'}`}>
                      {isFileGroup
                        ? (setName ? setName : (isGrouped ? `${viewCount} Pivot Views` : primaryView.name))
                        : primaryView.name}
                    </h3>
                    {!isEnabled && (
                      <span className="px-2 py-0.5 text-xs font-body-bold text-error-700 bg-error-50 rounded-md">
                        Disabled
                      </span>
                    )}
                    {isGrouped && (
                      <span className="px-2 py-1 text-xs font-body-bold text-brand-700 bg-brand-50 rounded-md">
                        {viewCount} tabs
                      </span>
                    )}
                    {isFileGroup && fileId && (
                      <button
                        type="button"
                        onClick={() => handleRenameGroup(fileId, views)}
                        className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-neutral-100 text-subtext-color hover:text-default-font transition-colors"
                        title="Rename"
                        disabled={!isEnabled}
                      >
                        <FeatherEdit className="w-4 h-4" />
                      </button>
                    )}
                    <span className="text-sm text-subtext-color">
                      Created: {formatDate(primaryView.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {fileId && (
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(fileId, isEnabled)}
                        disabled={isToggling}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                          isEnabled ? 'bg-brand-600' : 'bg-neutral-300'
                        } ${isToggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        title={isEnabled ? 'Disable register set' : 'Enable register set'}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            isEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                    {isEnabled ? (
                      <Button
                        variant="brand-primary"
                        size="small"
                        onClick={() => handleUseWithNewData(primaryView.id)}
                      >
                        Use with New Data
                      </Button>
                    ) : (
                      <Button
                        variant="neutral-secondary"
                        size="small"
                        onClick={() => fileId && handleToggleStatus(fileId, false)}
                        disabled={isToggling}
                      >
                        {isToggling ? 'Enabling...' : 'Enable'}
                      </Button>
                    )}
                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      icon={<FeatherTrash2 />}
                      onClick={() => isGrouped ? handleDeleteGroup(views) : handleDelete(primaryView.id, primaryView.name)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                {/* View Names List (for grouped views) */}
                {isGrouped && (
                  <div className="mb-4 flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-subtext-color">Tabs:</span>
                    {views.map((view, idx) => (
                      <span key={view.id} className="px-2 py-1 text-sm bg-neutral-100 rounded-md text-default-font">
                        {view.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Uploaded Files Table */}
                <div className="border border-neutral-border rounded-md overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-neutral-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-body-bold font-body-bold text-default-font">Period</th>
                        <th className="px-4 py-3 text-left text-body-bold font-body-bold text-default-font">Uploaded Date</th>
                        <th className="px-4 py-3 text-right text-body-bold font-body-bold text-default-font">Export</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fileHistory.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-4 py-6 text-center text-subtext-color">
                            No files uploaded yet. Click "Use with New Data" to upload files.
                          </td>
                        </tr>
                      ) : (
                        fileHistory.map((fileEntry) => (
                          <tr key={fileEntry.id} className="border-t border-neutral-border hover:bg-neutral-50">
                            <td className="px-4 py-3 text-body font-body text-default-font">
                              {fileEntry.period || '-'}
                            </td>
                            <td className="px-4 py-3 text-body font-body text-subtext-color">
                              {formatDate(fileEntry.usedAt)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="brand-secondary"
                                size="small"
                                icon={<FeatherDownload />}
                                onClick={() => handleExportWithFile(primaryView, fileEntry.fileId)}
                              >
                                Export
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

